import { ecorEnv } from '@/../environments/environment';
import { PortalUser } from '@/common/components/ecorr-store/portal-user/portal-user';
import { portalUserSelector } from '@/common/components/ecorr-store/portal-user/portal-user.selector';
import { PortalUserState } from '@/common/components/ecorr-store/portal-user/portal-user.state';
import { ParadataEventEnum, ParadataEventService } from '@/common/paradata-event/paradata-event.service';
import { EcorrUtilsService } from '@/common/utils/ecorr-utils.service';
import { EventService } from '@/surveys/services/event.service';
import { Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, ViewChild, signal, computed, inject, DestroyRef } from '@angular/core';
import { Validators, ReactiveFormsModule, FormGroup, FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AddSurveyDialogData } from '../../dialogs-data/add-survey-dialog-data';
import { AddSurveyDialogHttpService } from '../../services/add-survey-dialog-http.service';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { NgxMaskModule } from 'ngx-mask';

@Component({
    selector: 'app-add-survey-dialog',
    templateUrl: './add-survey-dialog.component.html',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatDialogModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        NgxMaskModule
    ],
    providers: [
        ParadataEventService,
        AddSurveyDialogHttpService,
        EcorrUtilsService
    ]
})
export class AddSurveyDialogComponent implements OnInit, OnDestroy {
    private destroyRef = inject(DestroyRef);
    private paradataEventService = inject(ParadataEventService);
    private store = inject(Store<PortalUserState>);
    private addSurveyDialogHttpService = inject(AddSurveyDialogHttpService);
    private matDialogRef = inject(MatDialogRef<AddSurveyDialogComponent>);
    private ecorrUtilsService = inject(EcorrUtilsService);
    private eventService = inject(EventService);

    @Inject(MAT_DIALOG_DATA)
    public addSurveyDialogData: AddSurveyDialogData = {
        title: 'ENTER AUTHENTICATION CODE',
        cancelButtonLabel: 'Cancel Request',
        confirmButtonLabel: 'Begin'
    };

    @ViewChild('authCode')
    authCode: ElementRef;
    @ViewChild('begin')
    begin: ElementRef;

    // Signals for reactive state
    private user = signal<PortalUser | null>(null);
    private addSurveyError = signal<string>('');
    private addSurveyInfo = signal<string>('Enter and submit your unique 12-digit code below.');
    private success = signal<boolean>(false);
    private updateMessage = signal<string>('');
    private addSurveyButtonDisabled = signal<boolean>(false);
    private hideHowDoIMsg = signal<boolean>(true);
    private surveyAdded = signal<string>('');
    private surveyTypeAdded = signal<string>('');
    private preCanvasSurvey = signal<boolean>(false);
    private surveyInstanceId = signal<number | null>(null);
    private dirty = signal<boolean>(false);
    private isPaperless = signal<boolean>(false);

    // Computed properties
    currentUser = computed(() => this.user());
    currentAddSurveyError = computed(() => this.addSurveyError());
    currentAddSurveyInfo = computed(() => this.addSurveyInfo());
    isSuccess = computed(() => this.success());
    currentUpdateMessage = computed(() => this.updateMessage());
    isAddSurveyButtonDisabled = computed(() => this.addSurveyButtonDisabled());
    isHideHowDoIMsg = computed(() => this.hideHowDoIMsg());
    currentSurveyAdded = computed(() => this.surveyAdded());
    currentSurveyTypeAdded = computed(() => this.surveyTypeAdded());
    isPreCanvasSurvey = computed(() => this.preCanvasSurvey());
    currentSurveyInstanceId = computed(() => this.surveyInstanceId());
    isDirty = computed(() => this.dirty());
    isPaperlessSurvey = computed(() => this.isPaperless());

    cpassEnabled = ecorEnv.cpassEnabled;
    iePlaceholder = '____-____-____';

    constructor() {
        this.store.select(portalUserSelector)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(user => {
                this.user.set(user);
            });
        this.matDialogRef.disableClose = true;
    }

        formGroup!: FormGroup<{
            authCode: FormControl<string | null>;
        }>;

    showHelpModal() {
        this.paradataEventService.add(ParadataEventEnum.HowDoI);
        window.open(ecorEnv.faqPageLink, '_target');
    }

    fixIE($event) {
        this.formGroup.get('authCode').setValue(this.trim($event.target.value.toUpperCase()));
    }

    trim(value: string) {
        value = value.replace(/[^0-9A-Z]/g, '');

        if (value.length > 12) {
            value = value.substr(0, 12);
        }

        let str = value;
        if (str[4] && str[4] !== '-') {
            str = str.substring(0, 4) + '-' + str.substring(4, str.length);
        }
        if (str[9] && str[9] !== '-') {
            str = str.substring(0, 9) + '-' + str.substring(9, str.length);
        }

        return str;
    }

    confirm() {
        this.dirty.set(true);
        let authCode = this.formGroup.get('authCode').value;
        const currentUser = this.currentUser();

        if (this.isIE()) {
            authCode = authCode.replace(/-/g, '');
        }

        if (authCode.length !== 12) {
            return;
        }
        this.addSurveyButtonDisabled.set(true);
        
        if (currentUser) {
            this.addSurveyDialogHttpService.addSurvey(authCode.toUpperCase(), currentUser.userAccountId)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: (response) => {
                        if (response.length > 0) {
                            const card = response[0];
                            this.surveyAdded.set(card.displayName);
                            this.surveyTypeAdded.set(card.surveyTypeName);
                            this.preCanvasSurvey.set(card.preCanvasFlag);
                            this.surveyInstanceId.set(card.surveyInstanceId);
                            this.success.set(true);
                            this.addSurveyButtonDisabled.set(false);
                            if (card.surveyInstanceId) {
                                this.eventService.emitEvent({
                                    surveyInstanceId: card.surveyInstanceId
                                });
                                this.paradataEventService.add(ParadataEventEnum.AddAuthenticationCode, card.surveyId);
                            }

                            if (card.paperlessOptionIndicator && card.paperlessOptionIndicator === 'Y') {
                                this.isPaperless.set(true);
                            }

                            if (card.canisterTrigger && card.canisterTrigger.includes('AuthCodeBurned')) {
                                this.eventService.emitEvent({
                                    canisterTriggered: {
                                        surveyId: card.surveyId,
                                        canisterCode: card.canisterCode,
                                        trigger: 'AuthCodeBurned'
                                    }
                                });
                            }
                        }
                    },
                    error: (error: any) => {
                        console.error('add-survey-dialog addSurvey error', error);

                        this.addSurveyButtonDisabled.set(false);
                        this.success.set(false);
                        this.hideHowDoIMsg.set(false);

                        if (error.error.message === 'EC-102') {
                            this.addSurveyError.set('Invalid authentication code. Survey could not be added.');
                            this.hideHowDoIMsg.set(false);
                            this.paradataEventService.add(ParadataEventEnum.InvalidAuthenticationCode);
                        } else if (error.error.message === 'EC-143' || error.error.message === 'EC-131') {
                            this.addSurveyError.set('Authentication Code is expired or the survey period has closed.');
                            this.hideHowDoIMsg.set(false);
                        } else if (error.error.message === 'EC-115') {
                            this.addSurveyError.set('Authentication Code already used.');
                            this.hideHowDoIMsg.set(false);
                            this.paradataEventService.add(ParadataEventEnum.UsedAuthenticationCode);
                        } else if (error.error.message === 'EC-129') {
                            this.addSurveyError.set('Survey could not be added. Please try again.');
                            this.hideHowDoIMsg.set(true);
                        } else if (error.error.message === 'EC-130') {
                            this.addSurveyError.set('Authentication Code is disabled. Please contact the survey help desk for assistance.');
                            this.hideHowDoIMsg.set(true);
                            this.paradataEventService.add(ParadataEventEnum.CancelAuthenticationCodeRequest);
                        } else if (error.error.message === 'EC-153') {
                            this.addSurveyError.set(' Attempt to burn Unauthorized Code.');
                            this.hideHowDoIMsg.set(false);
                            this.paradataEventService.add(ParadataEventEnum.UnauthorizedCode);
                        } else {
                            this.addSurveyError.set('Survey could not be added. Please try again.');
                            this.hideHowDoIMsg.set(true);
                        }
                        this.addSurveyButtonDisabled.set(false);
                    }
                });
        }
    }

    ngOnInit() {
        this.paradataEventService.add(ParadataEventEnum.BeginbuttonforAuthenticationCode);
        
        // Ensure dialog data is properly initialized
        if (!this.addSurveyDialogData) {
            this.addSurveyDialogData = {
                title: 'Add Survey',
                cancelButtonLabel: 'Cancel',
                confirmButtonLabel: 'Begin'
            };
        }
        
        if (this.isIE()) {
            this.formGroup = new FormGroup({
                authCode: new FormControl('', [Validators.required, Validators.minLength(14)])
            });
            setTimeout(() => {
                if (this.begin && this.begin.nativeElement) {
                    this.begin.nativeElement.click();
                }
            }, 150);
        } else {
            this.formGroup = new FormGroup({
                authCode: new FormControl('', [Validators.required, Validators.minLength(12)])
            });
            setTimeout(() => {
                if (this.authCode && this.authCode.nativeElement) {
                    this.authCode.nativeElement.click();
                }
            }, 150);
        }
    }

    fixCursorPos() {
        this.dirty.set(true);
        if (this.authCode && this.authCode.nativeElement) {
            this.authCode.nativeElement.click();
        }
    }

    isIE() {
        return this.ecorrUtilsService.isIE();
    }

    cancel() {
        this.paradataEventService.add(ParadataEventEnum.CancelAuthenticationCodeRequest);
        if (this.isPaperlessSurvey() || this.isPreCanvasSurvey()) {
            this.close({
                'paperless': this.isPaperlessSurvey(),
                'surveyType': this.currentSurveyTypeAdded(),
                'surveyName': this.currentSurveyAdded(),
                'preCanvasSurvey': this.isPreCanvasSurvey(),
                'surveyInstanceId': this.currentSurveyInstanceId()
            });
        } else {
            this.close('cancel');
        }
    }

    close(value: any) {
        this.matDialogRef.close(value);
    }

    @HostListener('keydown.esc')
    public onEsc() {
        this.close('ESC keydown');
    }

    ngOnDestroy() {
        this.paradataEventService.flushParadataevents();
    }
}